/**
 * Presentation metadata for quality-gate issue codes.
 *
 * Mirrors the codes emitted by evaluatePost() in
 * firebase/functions/services/blog/qualityGate.js — keep the two in sync.
 * (Same contract as refreshMeta.js, which mirrors REFRESH_CHANGE_TYPES.)
 *
 * The backend message is always authoritative and is what gets rendered; this
 * map only supplies the label and styling so a reviewer can scan severity at a
 * glance instead of reading every line.
 */

const SEVERITY_STYLES = {
  error: {
    icon: "❌",
    label: "Error",
    className: "bg-red-50 text-red-700 border-red-200",
  },
  warning: {
    icon: "⚠️",
    label: "Warning",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
};

/** Short human label per issue code, for the badge beside the message. */
const CODE_LABELS = {
  missing_table: "No table",
  malformed_html: "Broken HTML",
  unexpected_html_tags: "Off-contract tags",
  no_internal_links: "No internal links",
  too_few_internal_links: "Too few internal links",
  missing_title: "No title",
  title_too_long: "Title too long",
  missing_description: "No meta description",
  description_too_long: "Meta description too long",
  no_targeted_keywords: "No keywords",
  word_count_low: "Too short",
  word_count_high: "Too long",
  keyword_not_in_title: "Keyword not in title",
  keyword_not_in_intro: "Keyword not in intro",
  keyword_not_in_heading: "Keyword not in heading",
  keyword_not_in_description: "Keyword not in description",
};

/**
 * Codes the backend "Fix now" pass will actually attempt.
 *
 * Mirrors FIXABLE_CODES (REPAIRABLE) in
 * firebase/functions/services/blog/repairPolicy.js — keep the two in sync.
 *
 * The internal-link codes are absent by design, not by omission: the backend
 * repairs those deterministically BEFORE the AI pass, so if they still appear
 * in a report it means no link targets were available and no amount of
 * repairing will invent one.
 */
const FIXABLE_CODES = new Set([
  "missing_table",
  "missing_title",
  "title_too_long",
  "missing_description",
  "no_targeted_keywords",
  "malformed_html",
  "word_count_low",
  "word_count_high",
  "unexpected_html_tags",
  "description_too_long",
  "keyword_not_in_title",
  "keyword_not_in_intro",
  "keyword_not_in_heading",
  "keyword_not_in_description",
]);

export function severityMeta(severity) {
  return SEVERITY_STYLES[severity] || SEVERITY_STYLES.warning;
}

/**
 * Will the automated fix attempt this code?
 *
 * Drives whether the reviewer is offered a "Fix now" button and which issues
 * are labelled manual — promising a fix the backend will not attempt is worse
 * than offering no button at all.
 */
export function isFixable(code) {
  return FIXABLE_CODES.has(code);
}

/** True when at least one issue in the report can be fixed automatically. */
export function hasFixableIssue(issues) {
  return Array.isArray(issues) && issues.some((i) => isFixable(i?.code));
}

/**
 * Unknown codes fall back to the raw code rather than being hidden — a new
 * backend check must never render as a blank badge.
 */
export function codeLabel(code) {
  return CODE_LABELS[code] || code;
}
